import React, { useContext } from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import {
  createHashRouter,
  RouterProvider,
  redirect,
  Outlet,
} from "react-router-dom";
import {
  Coverage,
  CoverageDoctors,
  HomePage,
  PlanDetailsPage,
  Preferences,
  RecommendedPage,
} from "./routes";
import AllPlans from "./routes/all_plans";
import ComparePage from "./routes/compare";
import CoverageYear from "./routes/coverage_year";
import { useSessionStorage } from "@uidotdev/usehooks";
import { getHealthPlan } from "./api/getHealthPlan";
import { getHealthPlans } from "./api/getHealthPlans";
import { IEnrollData, IPlan, IPlans } from "./interfaces/api";
import ErrorPage from "./routes/error-page";
import { yearAndStringResources } from "./api/getYearAndStringResources";

async function getSelectedYear(zip: string) {
  const zipYears = await yearAndStringResources.getYearsForZip(zip);
  let selectedYear = Number(sessionStorage.getItem("planYear"));

  if (zipYears.find((year) => year.Year === selectedYear) !== undefined) {
    return Number(selectedYear);
  }

  selectedYear = zipYears.reduce(
    (highest, year) => (highest > year.Year ? highest : year.Year),
    0
  );
  sessionStorage.setItem("planYear", `${selectedYear}`);

  return selectedYear;
}

function getQueryParams() {
  const params: Record<string, number> = {};
  const storageToParamKeys = {
    isVeteran: "isveteran",
    isCSNP: "iscsnp",
    isCoverage: "isdsnp",
    isHMO: "HMO",
    isPPO: "PPO",
  };

  Object.entries(storageToParamKeys).forEach(([storageKey, paramKey]) => {
    const value = sessionStorage.getItem(storageKey);

    if (value !== null) {
      params[paramKey] = ["true", "1"].includes(value) ? 1 : 0;
    }
  });

  return params;
}

const router = createHashRouter([
  {
    path: "/",
    element: <HomePage />,
    errorElement: <ErrorPage />,
  },
  {
    element: <Outlet />,
    id: "zipRoot",
    errorElement: <ErrorPage />,
    async loader() {
      const zip = sessionStorage.getItem("zipCode");
      if (zip === null || !/\d{5}/.test(zip)) {
        return redirect("/");
      }

      let plans: IPlans = {
        Plans: [] as IPlan[],
        StringResource: [] as IEnrollData[],
      };

      const [selectedYear, yearsForZip, enrollmentLink] = await Promise.all([
        getSelectedYear(zip),
        yearAndStringResources.getYearsForZip(zip),
        yearAndStringResources.getStringResource("EnrollmenLinkLive"),
      ]);

      if (yearsForZip.length > 0) {
        const params = getQueryParams();
        const result = await getHealthPlans(selectedYear, zip, params);

        if (result !== null && result.Plans.length) {
          plans = result;
        }
      }

      return {
        enrollmentLink,
        plans,
        selectedYear,
        yearsForZip,
        zip,
      };
    },
    children: [
      {
        path: "/get-recommendations/coverage-year",
        element: <CoverageYear />,
      },
      {
        path: "/get-recommendations/coverage",
        element: <Coverage />,
      },
      {
        path: "/get-recommendations/preferences",
        element: <Preferences />,
      },
      {
        path: "/get-recommendations/add-your-doctors",
        element: <CoverageDoctors />,
      },
      {
        path: "/recommended-plans",
        element: <RecommendedPage />,
        async loader() {
          const zip = sessionStorage.getItem("zipCode") as string;
          const params = getQueryParams();
          const year = await getSelectedYear(zip);

          const allPlans = await getHealthPlans(year, zip, params);

          const comparePlans =
            allPlans !== null && Array.isArray(allPlans?.Plans)
              ? await Promise.all(
                  allPlans.Plans.slice(0, 3).map((plan) => {
                    return getHealthPlan(zip, year, {
                      hcode: plan.hcode,
                      pbpid: plan.pbp,
                    });
                  })
                )
              : [];

          return {
            plans: comparePlans,
          };
        },
      },
      {
        path: "/all-plans",
        element: <AllPlans />,
      },
      {
        path: "/compare-plans",
        element: <ComparePage />,
        async loader() {
          const zip = sessionStorage.getItem("zipCode");
          if (zip === null || !/\d{5}/.test(zip)) {
            return redirect("/");
          }

          const dataArray = (
            sessionStorage.getItem("dataArray") !== null
              ? JSON.parse(sessionStorage.getItem("dataArray") as string).slice(
                  0,
                  3
                )
              : []
          ) as string[];
          const hcidArray = dataArray.reduce<
            Array<{ hcode: string; pbpid: string }>
          >((acc, item) => {
            const parts = item.split("||")?.at(1)?.split("_");

            if (parts !== undefined && parts.length === 2) {
              acc.push({
                hcode: parts[0],
                pbpid: parts[1],
              });
            }

            return acc;
          }, []);

          const year = await getSelectedYear(zip);
          const plans = await Promise.all(
            hcidArray.map((item) => getHealthPlan(zip, year, item))
          );

          return {
            plans,
          };
        },
      },
      {
        path: "/plan-details",
        element: <PlanDetailsPage />,
        async loader({ request }) {
          const searchParams = new URL(request.url).searchParams;
          const hcode = searchParams.get("hcode");
          const pbpid = searchParams.get("pbpid");

          if (!hcode || !pbpid) {
            return redirect("/");
          }

          const zip = sessionStorage.getItem("zipCode") as string;
          const year = await getSelectedYear(zip);

          const plan = await getHealthPlan(zip, year, {
            hcode,
            pbpid,
          });

          return {
            plan,
          };
        },
      },
    ],
  },
]);

const AppContext = React.createContext<{
  sticky: any[];
  setSticky: React.Dispatch<React.SetStateAction<any[]>>;
}>({
  sticky: [],
  setSticky: () => {},
});

export function useTheme() {
  return useContext(AppContext);
}

const App = () => {
  const [sticky, setSticky] = useSessionStorage<any[]>("dataArray", []);

  return (
    <AppContext.Provider
      value={{
        sticky,
        setSticky,
      }}
    >
      <RouterProvider router={router} />
    </AppContext.Provider>
  );
};

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);
root.render(<App />);
